import { isKeyOf } from "../../../../types/internal/utility";
import { getTempOption, hasTempOption, setTempOption } from "../../Platform/Options/Options";
import { type HostConnectionTypeEnum } from "./HostTypes";

export interface LocalHostConnection {
    type: HostConnectionTypeEnum;
    priceSet: string | null;
    settings: Record<string, unknown>;
}

/**
 * Get the list of host connection ids for the provided outlet id
 * @param id
 */
export const getOutletHostConnections = (id: string): Array<string> => {
    return getTempOption(`host_connection_outlet_${id}`, []);
};

/**
 * Get the type and settings of the provided host connection id
 * @param id
 */
export const getHostConnection = (id: string): null | LocalHostConnection => {
    return getTempOption(`host_connection_${id}`, null) as null | LocalHostConnection;
};

/**
 * Check to see if the host connections are loaded
 */
export const areHostConnectionsLoaded = (): boolean => {
    return hasTempOption("host_connection_exists");
};

/**
 * Check to see if the vendor has host connections
 */
export const hasHostConnections = (): boolean => {
    return getTempOption("host_connection_exists", false);
};

/**
 * Format and store the host connections
 * @param hostConnections
 */
export const storeHostConnections = (hostConnections: Array<{
    id: string;
    type: HostConnectionTypeEnum;
    outlet: string;
    priceSet: string | null;
    settings: Record<string, unknown>;
}>): void => {
    const outletMap: Record<string, Array<string>> = {};

    // Prepare the outlet map and store the host connection
    for(const { id, type, outlet, settings, priceSet } of hostConnections) {
        if(!isKeyOf(outletMap, outlet)) {
            outletMap[outlet] = [];
        }

        outletMap[outlet].push(id);

        setTempOption(`host_connection_${id}`, {
            type,
            priceSet,
            settings,
        } satisfies LocalHostConnection);
    }

    // Store the outlet mapping
    for(const outlet in outletMap) {
        if(!isKeyOf(outletMap, outlet)) {
            continue;
        }

        setTempOption(`host_connection_outlet_${outlet}`, outletMap[outlet]);
    }

    // Store whether there are host connections
    setTempOption("host_connection_exists", hostConnections.length > 0);
};
